import {
  Card,
  Text,
  Stack,
  LoadingOverlay,
  Container,
  Group,
  Badge,
  Title,
  Button,
  Divider,
  ScrollArea
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import ChipComponent from 'components/ChipComponent';
import ModalPopupComponent from 'components/ModalPopup';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import React, { useState, useEffect } from 'react';
import { masterQuestionService } from 'services/lia/masterQuestionService';
import { masterGroupService } from 'services/lia/masterGroupService';
import { MasterQuestion } from 'services/api';
import PageTitle from 'components/PageTitle';
import SecondaryButton from 'components/SecondaryButton';
import { IconEdit, IconUpload } from '@tabler/icons-react';
import Toolbar from 'components/Toolbar';
import DataTables from 'components/DataTables';
import FilterModal from 'components/FilterModal';
import QuestionFilter, { QuestionFilterValues } from './components/QuestionFilter';
import ImportQuestionModal, { ImportQuestion } from './components/ImportQuestionModal';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import dayjs from 'dayjs';

const MasterQuestionPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const defaultSearch = new URLSearchParams(location.search).get('search') || '';

  // Keep track of previous search to detect changes
  const [previousSearch, setPreviousSearch] = useState(defaultSearch);

  const [opened, { open, close }] = useDisclosure();
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure();
  const [selectedQuestion, setSelectedQuestion] = useState<MasterQuestion | null>(null);
  const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
  const [importOpened, { open: openImport, close: closeImport }] = useDisclosure();

  const [filterOpened, { open: openFilter, close: closeFilter }] = useDisclosure();
  const [searchParams, setSearchParams] = useSearchParams();

  // Updated filter state to handle new API structure
  const [activeFilters, setActiveFilters] = useState<QuestionFilterValues>(() => {
    const typeParam = searchParams.get('type');
    const groupsParam = searchParams.get('groups');
    const singleGroupParam = searchParams.get('group'); // For backward compatibility

    let groups: string[] = [];

    if (groupsParam) {
      groups = groupsParam.split(',').filter(Boolean);
    } else if (singleGroupParam) {
      groups = singleGroupParam.split(',').filter(Boolean);
    }

    return {
      type: typeParam,
      groups: groups.length > 0 ? groups : null
    };
  });

  // Reset to page 1 when search changes
  useEffect(() => {
    if (defaultSearch !== previousSearch) {
      setCurrentPage(1);
      setPreviousSearch(defaultSearch);
    }
  }, [defaultSearch, previousSearch]);

  // Updated query to pass filters in correct format
  const {
    data: questionsData,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['masterQuestions', defaultSearch, currentPage, pageSize, activeFilters],
    queryFn: () =>
      masterQuestionService.getList({
        search: defaultSearch,
        page: currentPage,
        limit: pageSize,
        type: activeFilters.type || undefined,
        groups: activeFilters.groups || undefined // Pass as array
      }),
    refetchOnWindowFocus: false
  });

  const { data: groupsData } = useQuery({
    queryKey: ['masterGroups'],
    queryFn: () => masterGroupService.getList()
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterQuestionService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masterQuestions'] });
      close();
      showSuccessNotification({
        title: 'Question Deleted',
        message: 'Question has been successfully deleted.'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Delete Failed',
        message: error?.response?.data?.message || 'Failed to delete question.'
      });
    }
  });

  const [columns, setColumns] = useState([
    {
      heading: 'Question',
      props: (row: MasterQuestion) =>
        row.title.length > 50 ? row.title.substring(0, 50) + '...' : row.title,
      sortable: true,
      active: true,
      id: 'title'
    },
    {
      heading: 'Type of Question',
      props: 'type',
      sortable: true,
      active: true,
      id: 'type'
    },
    {
      heading: 'Group Question',
      props: 'groupName',
      sortable: true,
      active: true,
      id: 'groupName'
    },
    {
      heading: 'Created By',
      props: 'userIn',
      sortable: true,
      active: true,
      id: 'userIn'
    },
    {
      heading: 'Created Date',
      props: (row: MasterQuestion) => dayjs(row.dateIn).format('DD MMM YYYY HH:mm'),
      sortable: true,
      active: true,
      id: 'dateIn'
    },
    {
      heading: 'Updated By',
      props: (row: MasterQuestion) => row.userUp || '-',
      sortable: true,
      active: true,
      id: 'userUp'
    },
    {
      heading: 'Updated Date',
      props: (row: MasterQuestion) =>
        row.dateUp ? dayjs(row.dateUp).format('DD MMM YYYY HH:mm') : '-',
      sortable: true,
      active: true,
      id: 'dateUp'
    },
    {
      heading: 'Status',
      props: 'isDeleted',
      sortable: true,
      active: true,
      id: 'IsDeleted',
      render: (value: boolean) => (
        <ChipComponent
          status={value ? 'Inactive' : 'Active'}
          color={value ? 'red' : '#7B2183'}
          bgColor={value ? '#F6D3D1' : '#E5D3E6'}
        />
      )
    },
    {
      heading: 'Action',
      props: 'action',
      active: true,
      id: 'action'
    }
  ]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleView = (row: MasterQuestion) => {
    setSelectedQuestion(row);
    openDetail();
  };

  const handleEdit = (row: MasterQuestion) => {
    // Navigate in the same tab
    const url = `/app/lia/questions/master-questions/edit?id=${row.msQuestionID}`;
    navigate(url);
  };

  const handleDelete = (row: MasterQuestion) => {
    setQuestionToDelete(row.msQuestionID);
    open();
  };

  const handleCreateClick = () => {
    // Navigate in the same tab
    navigate('/app/lia/questions/master-questions/add');
  };

  const processDelete = async () => {
    if (questionToDelete) {
      await deleteMutation.mutateAsync(questionToDelete);
    }
  };

  // Updated filter apply handler
  const handleFilterApply = (filters: QuestionFilterValues) => {
    setActiveFilters(filters);
    setCurrentPage(1); // Reset to page 1 when applying filters

    const newParams = new URLSearchParams(searchParams);

    // Handle type filter
    if (filters.type && filters.type !== '') {
      newParams.set('type', filters.type);
    } else {
      newParams.delete('type');
    }

    // Handle groups filter
    if (filters.groups && filters.groups.length > 0) {
      newParams.set('groups', filters.groups.join(','));
    } else {
      newParams.delete('groups');
    }

    // Remove old single group param for backward compatibility
    newParams.delete('group');

    setSearchParams(newParams);
  };

  const copyMutation = useMutation({
    mutationFn: (id: string) => masterQuestionService.copy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masterQuestions'] });
      showSuccessNotification({
        title: 'Question Copied',
        message: 'Question has been successfully copied.'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Copy Failed',
        message: error?.response?.data?.message || 'Failed to copy question.'
      });
    }
  });

  const handleCopy = (row: MasterQuestion) => {
    copyMutation.mutateAsync(row.msQuestionID);
  };

  const importMutation = useMutation({
    mutationFn: async ({
      groupId,
      questions
    }: {
      groupId: string;
      questions: ImportQuestion[];
    }) => {
      const questionsToImport = questions.map((q) => ({
        title: q.title,
        description: q.description,
        type: q.type,
        category: q.category,
        skalaCategory: q.skalaCategory,
        rangeItems: q.rangeItems
      }));

      return masterQuestionService.bulkImport(groupId, questionsToImport);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['masterQuestions'] });

      if (result.failed > 0) {
        showErrorNotification({
          title: `Partial Import: ${result.successful}/${result.successful + result.failed} Successful`,
          message: `${result.successful} questions imported successfully. ${result.failed} failed to import. Check console for details.`
        });
      } else {
        showSuccessNotification({
          title: 'Import Successful! 🎉',
          message: `All ${result.successful} questions have been imported successfully and are ready to use.`
        });
      }
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Import Failed',
        message: error?.message || 'An unexpected error occurred during import. Please try again.'
      });
    }
  });

  const handleImport = async (groupId: string, questions: ImportQuestion[]) => {
    await importMutation.mutateAsync({ groupId, questions });
  };

  // Prepare data for DataTables component with proper pagination info
  const tableData = {
    data: questionsData?.data || [],
    pageNumber: questionsData?.pageNumber || 1,
    pageSize: questionsData?.pageSize || 10,
    totalPages: questionsData?.totalPages || 0,
    totalRecords: questionsData?.totalRecords || 0
  };

  // Calculate active filter count for display
  const activeFilterCount =
    (activeFilters.type ? 1 : 0) +
    (activeFilters.groups && activeFilters.groups.length > 0 ? 1 : 0);

  return (
    <Container fluid>
      <ModalPopupConfirmationComponent
        buttonText='Delete'
        statusProcess='delete'
        opened={opened}
        open={open}
        close={close}
        onSubmit={processDelete}
      />

      <ModalPopupComponent opened={detailOpened} close={closeDetail} size='xl' withCloseButton>
        {/* Detail modal content remains the same */}
        <Stack gap={0}>
          {/* Header */}
          <Group justify='space-between' align='flex-start' p='xl' pb='lg'>
            <Stack gap={4}>
              <Title order={2} c='dark'>
                Question Details
              </Title>
              <Text size='sm' c='dimmed'>
                View and manage question information
              </Text>
            </Stack>
            {selectedQuestion && (
              <Group gap='sm'>
                <Badge
                  color={selectedQuestion.isDeleted ? 'red' : 'green'}
                  variant='light'
                  size='lg'
                  radius='md'
                >
                  {selectedQuestion.isDeleted ? 'Inactive' : 'Active'}
                </Badge>
                <SecondaryButton
                  text='Edit Question'
                  onClick={() => {
                    handleEdit(selectedQuestion);
                    closeDetail();
                  }}
                  leftSection={<IconEdit size={16} />}
                />
              </Group>
            )}
          </Group>

          <Divider />

          {/* Content */}
          {selectedQuestion ? (
            <ScrollArea style={{ height: '60vh' }} p='xl'>
              <Stack gap='xl'>
                {/* Question Information */}
                <Card withBorder p='lg' radius='lg'>
                  <Stack gap='md'>
                    <Text fw={600} size='lg' c='dark'>
                      ❓ Question Information
                    </Text>

                    <Stack gap='sm'>
                      <Text size='sm' fw={500} c='dimmed'>
                        Question Title
                      </Text>
                      <Text fw={500} size='md' style={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {selectedQuestion.title}
                      </Text>
                    </Stack>

                    <Stack gap='sm'>
                      <Text size='sm' fw={500} c='dimmed'>
                        Question Description
                      </Text>
                      <Text size='sm' style={{ lineHeight: 1.6 }}>
                        {selectedQuestion.description || 'No description provided'}
                      </Text>
                    </Stack>

                    <Group grow>
                      <Stack gap='sm'>
                        <Text size='sm' fw={500} c='dimmed'>
                          Question Type
                        </Text>
                        <Badge variant='filled' color='blue' size='md'>
                          {selectedQuestion.type}
                        </Badge>
                      </Stack>

                      <Stack gap='sm'>
                        <Text size='sm' fw={500} c='dimmed'>
                          Question Category
                        </Text>
                        <Badge variant='outline' color='gray' size='md'>
                          {selectedQuestion.category || 'Uncategorized'}
                        </Badge>
                      </Stack>
                    </Group>
                  </Stack>
                </Card>

                {/* Group Information */}
                <Card withBorder p='lg' radius='lg'>
                  <Stack gap='md'>
                    <Text fw={600} size='lg' c='dark'>
                      📂 Group Information
                    </Text>

                    <Stack gap='sm'>
                      <Text size='sm' fw={500} c='dimmed'>
                        Assigned Group
                      </Text>
                      <Group gap='sm'>
                        <Badge variant='light' color='indigo' size='lg'>
                          {groupsData?.data?.find(
                            (group: any) =>
                              group.msGroupQuestionID === selectedQuestion.msGroupQuestionID
                          )?.title || 'Unknown Group'}
                        </Badge>
                      </Group>
                    </Stack>
                  </Stack>
                </Card>

                {/* Audit Information */}
                <Card withBorder p='lg' radius='lg'>
                  <Stack gap='md'>
                    <Text fw={600} size='lg' c='dark'>
                      📅 Audit Information
                    </Text>

                    <Group grow>
                      <Stack gap='sm'>
                        <Text size='sm' fw={500} c='dimmed'>
                          Created By
                        </Text>
                        <Group gap='xs'>
                          <Badge variant='light' color='blue'>
                            {selectedQuestion.userIn}
                          </Badge>
                        </Group>
                      </Stack>

                      <Stack gap='sm'>
                        <Text size='sm' fw={500} c='dimmed'>
                          Created Date
                        </Text>
                        <Text size='sm'>
                          {dayjs(selectedQuestion.dateIn).format('DD MMM YYYY, HH:mm')}
                        </Text>
                      </Stack>
                    </Group>

                    <Group grow>
                      <Stack gap='sm'>
                        <Text size='sm' fw={500} c='dimmed'>
                          Updated By
                        </Text>
                        <Group gap='xs'>
                          <Badge variant='light' color='gray'>
                            {selectedQuestion.userUp || 'N/A'}
                          </Badge>
                        </Group>
                      </Stack>

                      <Stack gap='sm'>
                        <Text size='sm' fw={500} c='dimmed'>
                          Updated Date
                        </Text>
                        <Text size='sm'>
                          {selectedQuestion.dateUp
                            ? dayjs(selectedQuestion.dateUp).format('DD MMM YYYY, HH:mm')
                            : 'Never updated'}
                        </Text>
                      </Stack>
                    </Group>
                  </Stack>
                </Card>

                {/* Range Items (if applicable) */}
                {selectedQuestion.rangeItems && selectedQuestion.rangeItems.length > 0 && (
                  <Card withBorder p='lg' radius='lg'>
                    <Stack gap='md'>
                      <Group justify='space-between' align='center'>
                        <Text fw={600} size='lg' c='dark'>
                          🎯 Range Options
                        </Text>
                        <Badge size='lg' variant='light' color='green'>
                          {selectedQuestion.rangeItems.length} options
                        </Badge>
                      </Group>

                      <Stack gap='sm'>
                        {selectedQuestion.rangeItems.map((item, index) => (
                          <Group
                            key={index}
                            justify='space-between'
                            p='sm'
                            style={{
                              backgroundColor: '#f8f9fa',
                              borderRadius: '8px',
                              border: '1px solid #e9ecef'
                            }}
                          >
                            <Group gap='sm'>
                              <Badge size='sm' color='blue' variant='filled'>
                                {item.sequence || index + 1}
                              </Badge>
                              <Text size='sm' fw={500}>
                                {item.label}
                              </Text>
                            </Group>
                            <Badge size='sm' color='gray' variant='outline'>
                              {item.value}
                            </Badge>
                          </Group>
                        ))}
                      </Stack>
                    </Stack>
                  </Card>
                )}
              </Stack>
            </ScrollArea>
          ) : (
            <Stack align='center' py='xl' gap='md'>
              <Text size='lg'>Loading question details...</Text>
            </Stack>
          )}
        </Stack>
      </ModalPopupComponent>

      <PageTitle
        title='Master Question'
        description='Create and manage assessment questions. Easily add, edit, or remove questions to build comprehensive question banks for your evaluations.'
      />

      <Card shadow='xs' radius='md' mt={20} pos='relative'>
        <LoadingOverlay
          visible={isLoading || isFetching || deleteMutation.isPending || copyMutation.isPending}
        />

        <Toolbar
          columns={columns}
          showSearch
          showCreateButton
          showFilter
          searchPlaceholder='Search questions...'
          createButtonText='Add New Question'
          filterButtonText={`Filter${activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}`}
          defaultSearchValue={defaultSearch}
          onCreate={handleCreateClick}
          showFilterColumn={true}
          setColumns={setColumns}
          onFilter={openFilter}
          additionalButtons={
            <Button leftSection={<IconUpload size={16} />} variant='outline' onClick={openImport}>
              Import Questions
            </Button>
          }
        />

        <ImportQuestionModal opened={importOpened} onClose={closeImport} onImport={handleImport} />

        <Stack mt={20}>
          <DataTables
            columns={columns}
            data={tableData.data}
            loading={isLoading}
            isNumbering
            activeSize={pageSize}
            sizing={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 }
            ]}
            withPagination
            totalData={tableData.totalRecords}
            currentPage={currentPage}
            currentSize={pageSize}
            handleChangePage={handlePageChange}
            handleChangeSize={handleSizeChange}
            withBulkData={false}
            handleView={handleView}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleCopy={handleCopy}
            emptyMessageTitle='No questions found'
            emptyMessageDescription='Create your first question by clicking the Add New Question button'
          />
        </Stack>
      </Card>

      <FilterModal opened={filterOpened} onClose={closeFilter} title='Filter Questions'>
        <QuestionFilter
          onApply={handleFilterApply}
          onClose={closeFilter}
          initialValues={activeFilters}
        />
      </FilterModal>
    </Container>
  );
};

export default MasterQuestionPage;
